import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getAiUsage, saveAiBudget } from '../../lib/aiUsageApi';
import {
  dayName,
  initialPeriodDraft,
  monthName,
  periodFromDraft,
  type PeriodDraft,
} from '../../lib/aiUsagePeriod';
import { usePermissions } from '../../hooks/usePermissions';
import { useI18n } from '../../contexts/I18nContext';
import { Select } from '../ui/Select';
import { DateField } from '../ui/DateField';
import { BudgetProgress } from './BudgetProgress';

// AI budget tab (SaaS hardening): current-month spend + the monthly EUR cap.
// The hard stop itself is enforced server-side at the AI transport layer;
// this screen only reads the summary and edits the AppConfig limit.

const AI_USAGE_KEY = ['ai-usage'] as const;

function formatEur(value: number): string {
  return value.toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatTokens(value: string): string {
  const n = Number(value);
  return Number.isFinite(n) ? n.toLocaleString('ro-RO') : value;
}

export function AiBudgetSettings() {
  const { t, locale } = useI18n();
  const qc = useQueryClient();
  const { can } = usePermissions();
  const canManageUsers = can('settings', 'canManageUsers');
  // Same fallback as the Settings tabs: older matrices may miss the flag.
  const canEditLocal = can('settings', 'canEditIntegrations') || canManageUsers;

  // What the per-model breakdown covers: a month (from the monthly aggregate,
  // complete for every month ever metered) or a from/to range (from the daily
  // aggregate, complete from `dailySince` on). The budget card always shows
  // the current month whatever is chosen here.
  const [draft, setPeriodDraft] = useState<PeriodDraft>(initialPeriodDraft);
  const period = useMemo(() => periodFromDraft(draft), [draft]);
  const rangeInvalid = draft.mode === 'range' && period === null;

  const query = useQuery({
    queryKey: [...AI_USAGE_KEY, period],
    queryFn: () => getAiUsage(period ?? undefined),
    // Keep the previous rows on screen while a new period loads.
    placeholderData: (prev) => prev,
    enabled: period !== null,
  });

  // Draft for the limit input: null = untouched (show server value).
  const [limitDraft, setLimitDraft] = useState<string | null>(null);
  const [status, setStatus] = useState<'idle' | 'saving' | 'done' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);

  const summary = query.data ?? null;
  // AI paid through the Firmio subscription: the limit is set in the Firmio Console.
  const managedByFirmio = summary?.budgetManagedBy === 'firmio';
  const canEdit = canEditLocal && !managedByFirmio;
  const limitValue = limitDraft ?? (summary?.budgetEur != null ? String(summary.budgetEur) : '');

  async function handleSave() {
    const trimmed = limitValue.trim();
    let limit: number | null = null;
    if (trimmed !== '') {
      const n = Number(trimmed.replace(',', '.'));
      if (!Number.isFinite(n) || n <= 0) {
        setError(t('aiBudget.limitInvalid'));
        setStatus('error');
        return;
      }
      limit = n;
    }
    setStatus('saving');
    setError(null);
    try {
      await saveAiBudget(limit);
      // The save answers with the default (this-month) breakdown, which may not
      // be the period on screen — refetch under the current key instead.
      await qc.invalidateQueries({ queryKey: AI_USAGE_KEY });
      setLimitDraft(null);
      setStatus('done');
    } catch (e) {
      setError(e instanceof Error ? e.message : t('settings.unknownError'));
      setStatus('error');
    }
  }

  const exceeded =
    summary != null && summary.budgetEur != null && summary.costEur >= summary.budgetEur;

  // The month picker lists every month with usage; the one on screen is kept
  // even when the list (from a stale summary) does not carry it yet.
  const monthOptions = useMemo(() => {
    const months = new Set([draft.month, ...(summary?.availableMonths ?? [])]);
    return [...months]
      .sort((a, b) => b.localeCompare(a))
      .map((m) => ({ value: m, label: monthName(m, locale) }));
  }, [draft.month, summary, locale]);

  // A range that starts before per-day metering began is incomplete — say so.
  const dailyNote =
    summary == null
      ? null
      : summary.dailySince == null
        ? t('aiBudget.dailyNone')
        : draft.from < summary.dailySince
          ? t('aiBudget.dailySince', { date: dayName(summary.dailySince, locale) })
          : null;

  return (
    <div className="max-w-3xl">
      <p className="text-sm text-gray-500">{t('aiBudget.intro')}</p>

      {query.isLoading && <p className="mt-6 text-sm text-gray-500">{t('aiBudget.loading')}</p>}
      {query.isError && (
        <p className="mt-6 text-sm text-red-600">
          {t('aiBudget.loadError')}
          {query.error instanceof Error ? `: ${query.error.message}` : ''}.
        </p>
      )}

      {summary && (
        <>
          {exceeded && (
            <p className="mt-6 rounded-md border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-600">
              {t('aiBudget.exceededBanner')}
            </p>
          )}

          <div className="mt-6 rounded-lg border border-gray-200 bg-white p-5">
            <h2 className="text-base font-semibold text-gray-900">
              {t('aiBudget.currentSpend', { month: summary.month })}
            </h2>
            <p className="mt-1 text-2xl font-bold text-gray-900">
              {formatEur(summary.costEur)} €
              {summary.budgetEur != null && (
                <span className="ml-2 text-sm font-normal text-gray-500">
                  {t('aiBudget.ofLimit', { limit: formatEur(summary.budgetEur) })}
                </span>
              )}
            </p>
            <p className="mt-1 text-sm text-gray-500">
              {t('aiBudget.calls', { count: summary.callCount })}
            </p>

            {summary.budgetEur != null && summary.budgetEur > 0 ? (
              <BudgetProgress
                spendEur={summary.costEur}
                budgetEur={summary.budgetEur}
                format={(v) => `${formatEur(v)} €`}
              />
            ) : (
              <p className="mt-3 text-sm text-gray-500">{t('aiBudget.noLimit')}</p>
            )}

            <div className="mt-5 flex flex-wrap items-end gap-3">
              <label className="block">
                <span className="block text-sm font-medium text-gray-700">
                  {t('aiBudget.limitLabel')}
                </span>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={limitValue}
                  onChange={(e) => {
                    setLimitDraft(e.target.value);
                    setStatus('idle');
                  }}
                  disabled={!canEdit}
                  placeholder="—"
                  className="mt-1 w-40 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none disabled:cursor-not-allowed disabled:bg-gray-50"
                />
                <span className="mt-1 block text-xs text-gray-500">
                  {managedByFirmio ? t('aiBudget.managedByFirmio') : t('aiBudget.limitHint')}
                </span>
              </label>
              {canEdit && (
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={status === 'saving'}
                  className="mb-5 inline-flex items-center rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {status === 'saving' ? t('aiBudget.saving') : t('aiBudget.save')}
                </button>
              )}
            </div>

            {status === 'done' && (
              <p className="mt-2 rounded-md border border-emerald-500/30 bg-emerald-500/10 p-3 text-sm text-emerald-600">
                {t('aiBudget.saved')}
              </p>
            )}
            {status === 'error' && (
              <p className="mt-2 rounded-md border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-600">
                {t('aiBudget.saveError')}
                {error ? `: ${error}` : ''}.
              </p>
            )}
          </div>

          <div className="mt-6 rounded-lg border border-gray-200 bg-white p-5">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <h2 className="text-base font-semibold text-gray-900">{t('aiBudget.perModel')}</h2>
                <p className="mt-1 text-sm text-gray-500">
                  {t('aiBudget.periodTotal', {
                    cost: formatEur(summary.periodCostEur),
                    count: summary.periodCallCount,
                  })}
                </p>
              </div>
              <div className="flex flex-wrap items-end gap-2">
                <div
                  role="radiogroup"
                  aria-label={t('aiBudget.perModel')}
                  className="inline-flex rounded-md border border-gray-200 bg-gray-50 p-0.5"
                >
                  {(['month', 'range'] as const).map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      role="radio"
                      aria-checked={draft.mode === mode}
                      onClick={() => setPeriodDraft((d) => ({ ...d, mode }))}
                      className={`rounded px-2.5 py-1.5 text-sm ${
                        draft.mode === mode
                          ? 'bg-white text-gray-900 shadow-sm'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      {t(`aiBudget.period.${mode}`)}
                    </button>
                  ))}
                </div>
                {draft.mode === 'month' ? (
                  <Select
                    label={t('aiBudget.period.monthLabel')}
                    value={draft.month}
                    onChange={(month) => month && setPeriodDraft((d) => ({ ...d, month }))}
                    options={monthOptions}
                    width={220}
                    className="w-44"
                  />
                ) : (
                  <>
                    <label className="block">
                      <span className="block text-xs font-medium text-gray-500">
                        {t('aiBudget.period.from')}
                      </span>
                      <DateField
                        value={draft.from}
                        max={draft.to || undefined}
                        onChange={(from) => setPeriodDraft((d) => ({ ...d, from }))}
                        required
                        aria-invalid={rangeInvalid}
                        className="mt-1"
                      />
                    </label>
                    <label className="block">
                      <span className="block text-xs font-medium text-gray-500">
                        {t('aiBudget.period.to')}
                      </span>
                      <DateField
                        value={draft.to}
                        min={draft.from || undefined}
                        onChange={(to) => setPeriodDraft((d) => ({ ...d, to }))}
                        required
                        aria-invalid={rangeInvalid}
                        className="mt-1"
                      />
                    </label>
                  </>
                )}
              </div>
            </div>
            {rangeInvalid && (
              <p className="mt-2 text-sm text-red-600">{t('aiBudget.rangeInvalid')}</p>
            )}
            {draft.mode === 'range' && dailyNote && (
              <p className="mt-2 rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-700">
                {dailyNote}
              </p>
            )}
            {summary.perModel.length === 0 ? (
              <p className="mt-3 text-sm text-gray-500">{t('aiBudget.perModelEmpty')}</p>
            ) : (
              <div className="mt-3 overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                      <th className="py-2 pr-4">{t('aiBudget.col.model')}</th>
                      <th className="py-2 pr-4">{t('aiBudget.col.provider')}</th>
                      <th className="py-2 pr-4 text-right">{t('aiBudget.col.calls')}</th>
                      <th className="py-2 pr-4 text-right">{t('aiBudget.col.inputTokens')}</th>
                      <th className="py-2 pr-4 text-right">{t('aiBudget.col.outputTokens')}</th>
                      <th className="py-2 text-right">{t('aiBudget.col.cost')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.perModel.map((row) => (
                      <tr
                        key={`${row.provider}:${row.model}`}
                        className="border-b border-gray-100 last:border-0"
                      >
                        <td className="py-2 pr-4 font-medium text-gray-900">{row.model}</td>
                        <td className="py-2 pr-4 text-gray-500">{row.provider}</td>
                        <td className="py-2 pr-4 text-right text-gray-700">{row.callCount}</td>
                        <td className="py-2 pr-4 text-right text-gray-700">
                          {formatTokens(row.inputTokens)}
                        </td>
                        <td className="py-2 pr-4 text-right text-gray-700">
                          {formatTokens(row.outputTokens)}
                        </td>
                        <td className="py-2 text-right font-medium text-gray-900">
                          {formatEur(row.costEur)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
